<script setup lang="ts">
import { computed, ref, onMounted, watch } from 'vue'
import PresentlevelsSubjectSummary from '@/presentlevels/PresentlevelsSubjectSummary.vue'
import { usePresentLevelsStore } from '@/presentlevels/presentlevels.store'
import PresentlevelsStudentHeader from './PresentlevelsStudentHeader.vue'
import { CONTENT_TYPES } from '@/util/access_visibility'
import LibraryRemoveConfirmDialog from '@/components/LibraryRemoveConfirmDialog.vue'
import { PL_SUMMARY_SAVING_STATES } from '@/presentlevels/presentlevels_constants'
import type { SubjectSummary } from '@/presentlevels/presentlevels_types'
import PresentlevelsAlignedGoalsPanel from './PresentlevelsAlignedGoalsPanel.vue'
import LibraryContentPanel from '@/components/LibraryContentPanel.vue'
import { storeToRefs } from 'pinia'
import { useLibraryStore } from '@/library/library.store.ts'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { EVENT_NAMES } from '@/tracking/analytics/event_names'
import { useLibraryFolderAnalytics } from '@/library/composables/useLibraryFolderAnalytics'

const presentLevelsStore = usePresentLevelsStore()
const libraryStore = useLibraryStore()
const { getEventData } = useLibraryFolderAnalytics()

const props = defineProps<{
  summary_uuid: string
  summary: SubjectSummary
}>()

const { currentFolder:libraryFolder} = storeToRefs(libraryStore)

const analytics = getAnalyticsTracker().getChildContext({
  plw_is_library: true,
  plw_statement_subject: props.summary?.subject_label,
  plw_session_enrolled_grade: props.summary?.grade_label,
})

const originalSummary = ref<SubjectSummary>({} as SubjectSummary)
const currentSummary = ref<SubjectSummary>({} as SubjectSummary)
const hasUnsavedChanges = ref(false)
const subjectSummaryRef = ref<InstanceType<typeof PresentlevelsSubjectSummary> | null>(null)

const saveStatus = computed(() => {
  const uuid = props.summary.uuid
  if (!uuid) return PL_SUMMARY_SAVING_STATES.NO_CHANGES
  return presentLevelsStore.savingStates[uuid] || PL_SUMMARY_SAVING_STATES.NO_CHANGES
})

onMounted(() => {
  const initialSummary = props.summary
  originalSummary.value = JSON.parse(JSON.stringify(initialSummary))
  currentSummary.value = JSON.parse(JSON.stringify(initialSummary))

  loadSummaryData()

  analytics.sendEvent(
    EVENT_NAMES.PRESENTLEVELS.LIBRARY_SUMMARY.VIEWED, getEventData.value
  )
})

const loadSummaryData = async () => {
  await Promise.all([presentLevelsStore.fetchCategoriesForSummarySubjects(), presentLevelsStore.fetchLevelOptions()])
}

const handleRemoveEvent = () => {
  analytics.sendEvent(
    EVENT_NAMES.PRESENTLEVELS.LIBRARY_SUMMARY.REMOVED, getEventData.value
  )
}

// Watch for changes in the store's summary and update currentSummary
watch(() => presentLevelsStore.subjectSummaries.find(s => s.uuid === props.summary.uuid), (storeSummary) => {
  if (storeSummary && props.summary.uuid) {
    currentSummary.value = { ...storeSummary }
  }
}, { deep: true })


watch(hasUnsavedChanges, (hasChanges) => {
  if (props.summary.uuid) {
    if (hasChanges) {
      presentLevelsStore.setSavingState(props.summary.uuid, PL_SUMMARY_SAVING_STATES.UNSAVED_CHANGES)
    } else {
      presentLevelsStore.setSavingState(props.summary.uuid, PL_SUMMARY_SAVING_STATES.NO_CHANGES)
    }
  }
})

const categoryOptions = computed(() => {
    if (props.summary) {
        return presentLevelsStore.getCategoriesForSubject(props.summary.subject_uuid)
    }
    return []
})

const saveChanges = async () => {
  if (!props.summary) return

  const fieldsToUpdate = {
    statement_text: currentSummary.value.statement_text,
    aligned_goals: currentSummary.value.aligned_goals,
    impact_statements: currentSummary.value.impact_statements,
    effect_statement: currentSummary.value.effect_statement,
    effect_statement_needs_update: currentSummary.value.effect_statement_needs_update
  }

  try {
    await presentLevelsStore.saveSubjectSummary(
      props.summary.uuid,
      fieldsToUpdate
    )
    const updatedSummary = presentLevelsStore.subjectSummaries.find(s => s.uuid === props.summary.uuid)
    if (updatedSummary) {
      // Stringify then parse to clone the objects
      currentSummary.value = JSON.parse(JSON.stringify(updatedSummary))
      originalSummary.value = JSON.parse(JSON.stringify(updatedSummary))
    }
    hasUnsavedChanges.value = false
    presentLevelsStore.clearNoNewAlignedGoalsFlag(props.summary.uuid)
  } catch (error) {
    console.error('Failed to save changes:', error)
  }
}

const discardChanges = async () => {
  if (window.confirm('Are you sure you want to discard any unsaved changes?')) {
    if (props.summary) {
      try {
        // Set flag to false FIRST to prevent it being set back to true by events
        hasUnsavedChanges.value = false
        
        await presentLevelsStore.updateSummary(originalSummary.value)
        currentSummary.value = JSON.parse(JSON.stringify(originalSummary.value))
        // Reset impact statements and statement text in child component
        if (subjectSummaryRef.value) {
          subjectSummaryRef.value.discardTextEdits(
            originalSummary.value.impact_statements || [],
            originalSummary.value.statement_text
          )
        }
        presentLevelsStore.clearNoNewAlignedGoalsFlag(props.summary.uuid)
      } catch (err) {
        console.error('Failed to discard changes:', err)
      }
    }
  }
}

</script>

<template>
  <div v-if="props.summary && libraryFolder?.uuid">
    <div class="flex flex-col lg:flex-row justify-center">
      <div class="relative">
        <PresentlevelsStudentHeader
          :student-name="props.summary.student_name"
          :grade-label="props.summary.grade_label || ''"
          :show-restart="false"
        />
        <PresentlevelsSubjectSummary
          ref="subjectSummaryRef"
          v-if="props.summary && categoryOptions.length > 0"
          :summary="currentSummary"
          :category-options="categoryOptions"
          :saved-summary="true"
          @text-changed="(text) => { currentSummary.statement_text = text; hasUnsavedChanges = true }"
          @impact-statements-changed="(statements) => { currentSummary.impact_statements = JSON.parse(JSON.stringify(statements)); hasUnsavedChanges = true }"
          @effect-statement-changed="(effectStatement) => { currentSummary.effect_statement = effectStatement; hasUnsavedChanges = true }"
          @effect-statement-needs-update-changed="(needsUpdate) => { currentSummary.effect_statement_needs_update = needsUpdate; hasUnsavedChanges = true }"
        />
      </div>
      <div class="flex flex-col">
        <div class="w-full md:w-plw-subcontent-panel ml-0 md:ml-4 mb-2">
          <LibraryContentPanel
            :content-uuid="summary.uuid"
            :content-type="CONTENT_TYPES.PRESENTLEVEL_STUDENT_SUMMARY"
            :save-status="saveStatus"
            :updated-at="summary.updated_at"
            @save="saveChanges"
            @discard="discardChanges"
            @remove="handleRemoveEvent"
          />
        </div>
        <PresentlevelsAlignedGoalsPanel
          :summaryUUID="props.summary_uuid"
        />
      </div>
    </div>
  </div>

  <LibraryRemoveConfirmDialog v-if="props.summary" :group="`${props.summary.uuid}_${libraryFolder?.uuid}`" />
</template>